import type { ImageTemplate } from '@/types/image-template';
import type { CanvasTemplateInstance } from '@/types/visual-editor';

export const DEFAULT_TEMPLATE_OUTPUT_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

type TemplateOutputCountSource =
  | Pick<ImageTemplate, 'model_params'>
  | Pick<CanvasTemplateInstance, 'modelParams'>
  | null
  | undefined;

function getModelParams(source: TemplateOutputCountSource): Record<string, unknown> {
  if (!source) {
    return {};
  }

  if ('model_params' in source) {
    return (source.model_params as Record<string, unknown>) || {};
  }

  if ('modelParams' in source) {
    return (source.modelParams as Record<string, unknown>) || {};
  }

  return {};
}

function clampTemplateOutputCountBounds(value: number): number {
  return Number.isSafeInteger(value) && value > 0 ? value : 1;
}

function readInteger(modelParams: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const rawValue = modelParams[key];
    const numericValue = typeof rawValue === 'number'
      ? rawValue
      : typeof rawValue === 'string' && rawValue.trim().length > 0
        ? Number(rawValue)
        : NaN;
    if (Number.isSafeInteger(numericValue) && numericValue > 0) {
      return clampTemplateOutputCountBounds(numericValue);
    }
  }
  return null;
}

function readIntegerArray(modelParams: Record<string, unknown>, ...keys: string[]): number[] {
  for (const key of keys) {
    const rawValue = modelParams[key];
    if (!Array.isArray(rawValue)) {
      continue;
    }

    const options = rawValue
      .map((item) => {
        const numericValue = typeof item === 'number'
          ? item
          : typeof item === 'string' && item.trim().length > 0
            ? Number(item)
            : NaN;
        return Number.isSafeInteger(numericValue) && numericValue > 0 ? clampTemplateOutputCountBounds(numericValue) : null;
      })
      .filter((item): item is number => item !== null);

    if (options.length > 0) {
      return Array.from(new Set(options)).sort((left, right) => left - right);
    }
  }

  return [];
}

export function resolveTemplateOutputCountOptions(source: TemplateOutputCountSource): number[] {
  const modelParams = getModelParams(source);
  const explicitOptions = readIntegerArray(
    modelParams,
    'output_count_options',
    'outputCountOptions',
  );
  // 旧配置仅提供快捷档位，不限制用户请求的总张数。
  const min = explicitOptions[0]
    ?? readInteger(modelParams, 'output_count_min', 'outputCountMin') ?? 1;
  const max = explicitOptions[explicitOptions.length - 1]
    ?? readInteger(modelParams, 'output_count_max', 'outputCountMax') ?? 8;
  const lower = Math.min(min, max);
  const upper = Math.max(min, max);
  // Avoid allocating an arbitrarily large preset menu; typed totals remain unrestricted.
  return upper - lower < 32
    ? Array.from({ length: upper - lower + 1 }, (_, index) => lower + index)
    : Array.from(new Set([lower, ...explicitOptions, upper]));
}

export function clampTemplateOutputCountValue(value: number, options: readonly number[]): number {
  if (Number.isSafeInteger(value) && value > 0) return value;
  return options.find((option) => Number.isSafeInteger(option) && option > 0)
    ?? DEFAULT_TEMPLATE_OUTPUT_COUNT_OPTIONS[0];
}

export function resolveTemplateDefaultOutputCount(source: TemplateOutputCountSource): number {
  const modelParams = getModelParams(source);
  const options = resolveTemplateOutputCountOptions(source);
  const configuredDefault = readInteger(
    modelParams,
    'default_output_count',
    'defaultOutputCount',
  );
  return clampTemplateOutputCountValue(configuredDefault ?? options[0], options);
}
